/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { flooringAreaValueNamePresenter } from '@kaiizen/kai-presenters';
import { type ReactNode, useId } from 'react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';

/**
 * One material's hatch: the ink drawn inside a `size`×`size` tile, repeated to
 * fill whatever it is painted on. Sizes are in the pattern's own user units,
 * so the same hatch reads at both swatch and full-plan scale.
 */
export type FlooringHatchPattern = {
  size: number;
  ink: ReactNode;
};

const INK = COLORS.ink;

/**
 * The materials the floor plan knows by name, drawn the way a plan set draws
 * them: a hatch that reads as the material rather than an arbitrary key.
 * Keyed by presented label, lowercased — `hardwoodArea` and `HardwoodArea`
 * both land on Hardwood.
 */
const NAMED_HATCH_PATTERNS: Record<string, FlooringHatchPattern> = {
  tile: { size: 10, ink: <path d='M0 0H10M0 0V10' stroke={INK} strokeWidth={0.6} /> },
  hardwood: { size: 6, ink: <line x1={0} y1={0.5} x2={14} y2={0.5} stroke={INK} strokeWidth={0.6} /> },
  carpet: {
    size: 6,
    ink: (
      <g fill={INK}>
        <circle cx={1.5} cy={1.5} r={0.5} />
        <circle cx={4.5} cy={3.5} r={0.5} />
        <circle cx={2.5} cy={5} r={0.5} />
      </g>
    ),
  },
  vinyl: { size: 5, ink: <path d='M0 5L5 0' stroke={INK} strokeWidth={0.5} /> },
  laminate: {
    size: 8,
    ink: (
      <g stroke={INK} strokeWidth={0.6}>
        <line x1={0} y1={0.5} x2={16} y2={0.5} />
        <line x1={8} y1={0.5} x2={8} y2={4} />
        <line x1={0} y1={4} x2={16} y2={4} />
        <line x1={12} y1={4} x2={12} y2={8} />
      </g>
    ),
  },
  stone: { size: 14, ink: <path d='M0 7H18M9 0V7M4 7V14M13 7V14' stroke={INK} strokeWidth={0.6} /> },
  concrete: {
    size: 16,
    ink: (
      <g>
        <circle cx={4} cy={5} r={0.7} fill={INK} />
        <circle cx={11} cy={10} r={0.7} fill={INK} />
        <path d='M9 3l1.5 1.5M4 11l1.5 1.5' stroke={INK} strokeWidth={0.5} />
      </g>
    ),
  },
};

/**
 * Hatches for materials outside the named set — orgs author their own flooring
 * types, so most projects carry at least one. Each differs from the named seven
 * and from every sibling on an axis coarse enough to survive a 22px swatch:
 * stroke direction, dot versus line, open versus crossed, continuous versus
 * broken. Order is the cycling order, so keep the most distinct ones first.
 */
const GENERIC_HATCH_PATTERNS: FlooringHatchPattern[] = [
  { size: 7, ink: <path d='M0 0L7 7' stroke={INK} strokeWidth={0.6} /> },
  { size: 8, ink: <path d='M0 8L8 0M0 0L8 8' stroke={INK} strokeWidth={0.55} /> },
  { size: 6, ink: <line x1={0.5} y1={0} x2={0.5} y2={12} stroke={INK} strokeWidth={0.6} /> },
  { size: 8, ink: <path d='M0 2H5M8 6H13' stroke={INK} strokeWidth={0.7} strokeLinecap='round' /> },
  { size: 10, ink: <path d='M0 7L5 2L10 7' stroke={INK} strokeWidth={0.65} fill='none' /> },
  { size: 9, ink: <circle cx={4.5} cy={4.5} r={2.1} stroke={INK} strokeWidth={0.6} fill='none' /> },
  { size: 11, ink: <path d='M5.5 2.2L8.6 7.6H2.4Z' stroke={INK} strokeWidth={0.6} fill='none' /> },
  {
    size: 16,
    ink: <path d='M1 1.5H7M1 4H7M1 6.5H7M9.5 9V15M12 9V15M14.5 9V15' stroke={INK} strokeWidth={0.6} />,
  },
  { size: 12, ink: <path d='M0 6Q3 2 6 6T12 6' stroke={INK} strokeWidth={0.6} fill='none' /> },
  { size: 12, ink: <path d='M6 3V9M3 6H9' stroke={INK} strokeWidth={0.55} /> },
];

/**
 * A hatch for every flooring-area value name. Named materials keep their own;
 * everything else takes the next generic pattern in list order, cycling once
 * a project carries more than ten unrecognized materials. Assignment is
 * positional rather than name-hashed so nothing collides before the cycle
 * wraps, and stable as long as the name list is.
 */
export const getFlooringHatchPatterns = (
  valueNames: readonly string[],
): Record<string, FlooringHatchPattern> => {
  let genericsUsed = 0;
  return Object.fromEntries(
    valueNames.map(valueName => [
      valueName,
      NAMED_HATCH_PATTERNS[flooringAreaValueNamePresenter(valueName).toLowerCase()]
        ?? GENERIC_HATCH_PATTERNS[genericsUsed++ % GENERIC_HATCH_PATTERNS.length],
    ]),
  );
};

const SWATCH_STYLE = css`
  label: FLOORING_HATCH_SWATCH_STYLE;
  display: block;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
`;

/**
 * The material's hatch as a small bordered square — the same hatch the floor
 * plan paints with, so a rollup row and the material on the plan read as the
 * same thing. Decorative: the material is always named in adjacent text.
 */
export const FlooringHatchSwatch = ({ pattern }: { pattern: FlooringHatchPattern }) => {
  // Pattern ids are referenced as URL fragments; useId's colons are not valid there.
  const patternId = `hatch${useId().replace(/:/g, '')}`;
  return (
    <svg css={SWATCH_STYLE} viewBox='0 0 22 22' aria-hidden='true'>
      <defs>
        <pattern
          id={patternId}
          width={pattern.size}
          height={pattern.size}
          patternUnits='userSpaceOnUse'
        >
          <rect width={pattern.size} height={pattern.size} fill={COLORS.white} />
          {pattern.ink}
        </pattern>
      </defs>
      <rect x='0.5' y='0.5' width='21' height='21' fill={`url(#${patternId})`} stroke={COLORS.stroke} />
    </svg>
  );
};
